import {
  ChainRail,
  PrimaryCta,
  Section,
  Seo,
  ShotFrame,
} from '@/components/primitives'
import { MotionVideo } from '@/components/MotionVideo'

const LINES = ['Medicare', 'ACA', 'Life', 'P&C']

type Solution = {
  id: string
  label: string
  title: string
  line: string
  chain: string[]
  chips: string[]
  note: string
  image: {
    src: string
    srcSet: string
    alt: string
  }
}

const SOLUTIONS: Solution[] = [
  {
    id: 'insurance',
    label: 'Insurance',
    title: 'Insurance ops that hold the line.',
    line: 'Lead to licensed agent to signed policy to commission. One record the whole way. Over 145,000,000 policies processed on the TLD stack.',
    chain: ['Lead', 'License check', 'Call', 'Docs', 'Policy', 'Commission'],
    chips: ['Medicare', 'ACA', 'Life', 'P&C'],
    note: 'Your routing rules apply. Wire your carriers and quoting stack. We keep the operation one.',
    image: {
      src: '/images/sol-insurance-1100w.webp',
      srcSet:
        '/images/sol-insurance-768w.webp 768w, /images/sol-insurance-1100w.webp 1100w, /images/sol-insurance-1600w.webp 1600w',
      alt: 'TLDNXS document repository for policy and compliance files',
    },
  },
  {
    id: 'contact-centers',
    label: 'Contact centers',
    title: 'Contact centers that can see the floor.',
    line: 'Who should take it, who took it, what happened next, on the same customer record. Queues, skills, and outcomes in one model.',
    chain: ['Demand', 'Queue', 'Agent', 'Call', 'Result', 'Follow-up'],
    chips: ['Queues', 'Skills', 'Live floor', 'Recordings'],
    note: 'Calling and texting need your providers connected.',
    image: {
      src: '/images/sol-contact-1100w.webp',
      srcSet:
        '/images/sol-contact-768w.webp 768w, /images/sol-contact-1100w.webp 1100w, /images/sol-contact-1600w.webp 1600w',
      alt: 'TLDNXS live agent floor monitoring view',
    },
  },
  {
    id: 'sales-acquisition',
    label: 'Growth & acquisition',
    title: 'Growth teams that know what actually closed.',
    line: 'Source and cost stay on every lead. See spend next to what closed, without losing the handoffs in between.',
    chain: ['Source', 'Lead', 'Route', 'Talk', 'Close', 'Attribute'],
    chips: ['Sources', 'Pipeline', 'Cost', 'Win rate'],
    note: 'High-volume intake depends on how your sources are wired.',
    image: {
      src: '/images/sol-sales-1100w.webp',
      srcSet:
        '/images/sol-sales-768w.webp 768w, /images/sol-sales-1100w.webp 1100w, /images/sol-sales-1600w.webp 1600w',
      alt: 'TLDNXS team operations board for sales performance',
    },
  },
  {
    id: 'in-person-sales',
    label: 'Field & distributed',
    title: 'Field sales on the same system as the office.',
    line: 'Route by territory, skill, and schedule. Every visit stays on the customer story your whole team can see.',
    chain: ['Lead', 'Territory', 'Route', 'Visit', 'Close'],
    chips: ['Territories', 'Routes', 'Appointments', 'Field teams'],
    note: 'We map fit to your field motion on the walkthrough, then configure the model.',
    image: {
      src: '/images/sol-inperson-1100w.webp',
      srcSet:
        '/images/sol-inperson-768w.webp 768w, /images/sol-inperson-1100w.webp 1100w, /images/sol-inperson-1600w.webp 1600w',
      alt: 'TLDNXS drive map centered on Austin, Texas for in-person sales routing',
    },
  },
]

export default function Solutions() {
  return (
    <>
      <Seo
        title="Solutions | TLDNXS Customer Operations Platform"
        description="Built for operations that can't afford dropped work. Over 145,000,000 policies processed on the TLD stack."
      />

      <section
        aria-labelledby="solutions-title"
        className="relative overflow-hidden border-b border-base-line"
      >
        <MotionVideo
          src="/videos/solutions-hero-web.mp4"
          poster="/videos/solutions-hero-poster.jpg"
          className="pointer-events-none absolute inset-0 opacity-25"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-base-950/85 via-base-950/75 to-base-950"
        />
        <div className="relative mx-auto w-full max-w-7xl px-5 py-16 text-center sm:px-8 md:py-24">
          <p className="eyebrow">Solutions</p>
          <h1
            id="solutions-title"
            className="mx-auto mt-4 max-w-3xl text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl"
          >
            Built for operations that can&rsquo;t afford dropped work.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-ink-mute md:text-lg">
            Same platform. Your sources, rules, documents, and floor. Backed by over 145,000,000 policies processed on the TLD stack.
          </p>
          <nav aria-label="Jump to a solution" className="mt-8">
            <ul className="flex flex-wrap items-center justify-center gap-2">
              {SOLUTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="btn-secondary py-2 text-xs">
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </section>

      {SOLUTIONS.map((solution, index) => {
        const imageLeft = index % 2 === 1
        return (
          <Section
            key={solution.id}
            id={solution.id}
            labelledBy={`${solution.id}-heading`}
            className={
              index % 2 === 0
                ? 'relative overflow-hidden bg-base-900/40'
                : 'relative overflow-hidden'
            }
          >
            {index === 0 ? (
              <MotionVideo
                src="/videos/solutions-lanes-web.mp4"
                poster="/videos/solutions-lanes-poster.jpg"
                className="pointer-events-none absolute inset-0 opacity-15"
              />
            ) : null}
            <div className="relative grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
              <div className={`text-center lg:col-span-5 ${imageLeft ? 'lg:order-2' : ''}`}>
                <p className="eyebrow">{solution.label}</p>
                <h2
                  id={`${solution.id}-heading`}
                  className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl"
                >
                  {solution.title}
                </h2>
                <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-ink-mute">
                  {solution.line}
                </p>

                {solution.id === 'insurance' ? (
                  <ul className="mt-5 flex flex-wrap items-center justify-center gap-2" aria-label="Insurance lines">
                    {LINES.map((line) => (
                      <li key={line} className="chip-signal">
                        {line}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="mt-5 flex flex-wrap items-center justify-center gap-2" aria-label="Focus areas">
                    {solution.chips.map((chip) => (
                      <li key={chip} className="chip-neutral">
                        {chip}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-8">
                  <p className="mono-label mb-3">How work moves</p>
                  <div className="flex justify-center">
                    <ChainRail
                      items={solution.chain}
                      ariaLabel={`${solution.label} operating chain`}
                    />
                  </div>
                </div>

                <p className="mx-auto mt-6 max-w-md text-xs leading-relaxed text-ink-dim">{solution.note}</p>
              </div>

              <div className={`lg:col-span-7 ${imageLeft ? 'lg:order-1' : ''}`}>
                <ShotFrame
                  src={solution.image.src}
                  srcSet={solution.image.srcSet}
                  sizes="(min-width: 1024px) 52vw, 100vw"
                  width={1100}
                  height={631}
                  alt={solution.image.alt}
                  tag="Real TLDNXS Omni · demo account"
                />
              </div>
            </div>
          </Section>
        )
      })}

      <Section labelledBy="solutions-cta-heading">
        <div className="mx-auto max-w-3xl text-center">
          <h2
            id="solutions-cta-heading"
            className="text-3xl font-extrabold tracking-tight text-ink sm:text-4xl"
          >
            Show us your operation.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-ink-mute md:text-lg">
            Where work comes in. Where it gets stuck. We map it live.
          </p>
          <div className="mt-8 flex justify-center">
            <PrimaryCta to="/walkthrough">Book a walkthrough</PrimaryCta>
          </div>
          <div className="mx-auto mt-10 max-w-md rounded-sm border border-base-line bg-base-900/80 p-6 text-center">
            <p className="mono-label">On the walkthrough</p>
            <ol className="mt-4 space-y-3 text-sm text-ink-mute">
              <li>Your sources and intake</li>
              <li>Who should get which work</li>
              <li>What “done” looks like for you</li>
            </ol>
          </div>
        </div>
      </Section>
    </>
  )
}
